import type { FreightType } from "../api/types.ts"

/**
 * 입고 스캔 직후에 적는 것 — 중량과 착/선불(착불이면 착불비).
 *
 * "이대로 저장할 수 있는가"와 "무엇을 보낼 것인가"를 한 곳에서 정한다.
 * 한도는 서버 검증(`server/src/routes/cases.ts` 의 weight·freight 스키마)과 맞춰 둔다 —
 * 여기서 걸러 두면 창고가 400 배너 대신 꺼진 버튼을 본다.
 */
export type ArrivalEntry = {
  /** 중량(g). 빈 값이면 적지 않은 것. */
  weight: string
  /** 고르지 않았으면 빈 값. */
  freightType: FreightType | ""
  /** 착불비(JPY). 빈 값이면 아직 모르는 것 — 나중에 적는다. */
  codFee: string
}

export type ArrivalSave = {
  weight: { grams: number; source: "manual" } | null
  freight: { freightType: FreightType; codFeeJpy: number | null } | null
}

const MAX_GRAMS = 1_000_000
const MAX_COD_FEE = 1_000_000

const isDigits = (value: string) => /^\d+$/.test(value)

/**
 * 보낼 것을 만든다. 적은 것이 없거나 숫자가 잘못됐으면 `null` — 저장 버튼을 끈다.
 */
export function arrivalSave(entry: ArrivalEntry): ArrivalSave | null {
  const grams = entry.weight === "" ? null : Number(entry.weight)
  if (entry.weight !== "") {
    if (!isDigits(entry.weight)) return null
    if (grams === null || grams <= 0 || grams > MAX_GRAMS) return null
  }

  // 선불에는 착불비를 적지 않는다(서버가 거부한다). 고르지 않았으면 착불비도 보내지 않는다.
  const collect = entry.freightType === "collect"
  if (collect && entry.codFee !== "") {
    if (!isDigits(entry.codFee)) return null
    if (Number(entry.codFee) > MAX_COD_FEE) return null
  }

  const weight = grams === null ? null : { grams, source: "manual" as const }
  const freight =
    entry.freightType === ""
      ? null
      : {
          freightType: entry.freightType,
          codFeeJpy: collect && entry.codFee !== "" ? Number(entry.codFee) : null,
        }

  if (weight === null && freight === null) return null
  return { weight, freight }
}
